import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

// ─── TipTap JSON → React Renderer ────────────────────────
// Shared by the chapter reader (reader-view) and the public wiki entry page so
// both surfaces render author content identically. Pure functions returning
// JSX — safe in server and client components alike. No "use client".
//
// Note: mention nodes deliberately render as plain text (just the name). The
// world wiki does NOT turn @mentions into inline links.

export type TipTapNode = {
    type: string;
    attrs?: Record<string, unknown>;
    content?: TipTapNode[];
    text?: string;
    marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
};

// Only these URL schemes are allowed in link hrefs. Author-supplied TipTap JSON
// (chapter bodies AND author's notes) reaches this renderer; without a scheme
// check a hand-crafted `javascript:` href would render into <a href> and execute
// on click (React renders such hrefs, only warning). Anything else renders as
// plain text — the link is dropped, not the words.
const SAFE_LINK_SCHEMES = new Set(["http:", "https:", "mailto:"]);

function safeHref(href: unknown): string | null {
    if (typeof href !== "string") return null;
    try {
        // Base lets protocol-relative/relative hrefs resolve for scheme checking.
        const url = new URL(href, "https://ilorisnovel.com");
        return SAFE_LINK_SCHEMES.has(url.protocol) ? href : null;
    } catch {
        return null;
    }
}

function textAlignmentClass(alignment: unknown): string | undefined {
    switch (alignment) {
        case "left": return "text-left";
        case "center": return "text-center";
        case "right": return "text-right";
        default: return undefined;
    }
}

/** True if the doc contains any non-whitespace text — used to skip rendering an
 *  empty container (e.g. an author's note that is just blank paragraphs). */
export function hasRenderableText(node: TipTapNode): boolean {
    if (node.type === "text" && node.text?.trim()) return true;
    return node.content?.some(hasRenderableText) ?? false;
}

function renderMarks(text: string, marks?: TipTapNode["marks"]): ReactNode {
    if (!marks || marks.length === 0) return text;

    let result: ReactNode = text;
    for (const mark of marks) {
        switch (mark.type) {
            case "bold":
                result = <strong>{result}</strong>;
                break;
            case "italic":
                result = <em>{result}</em>;
                break;
            case "underline":
                result = <u>{result}</u>;
                break;
            case "strike":
                result = <s>{result}</s>;
                break;
            case "code":
                result = <code>{result}</code>;
                break;
            case "link": {
                const href = safeHref(mark.attrs?.href);
                // Unsafe/invalid scheme → keep the text, drop the link.
                result = href ? (
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="text-primary underline hover:opacity-80"
                    >
                        {result}
                    </a>
                ) : (
                    result
                );
                break;
            }
        }
    }
    return result;
}

export function renderNode(node: TipTapNode, index: number): ReactNode {
    switch (node.type) {
        case "doc":
            return <>{node.content?.map((child, i) => renderNode(child, i))}</>;

        case "paragraph": {
            return (
                <p key={index} className={cn("leading-relaxed", textAlignmentClass(node.attrs?.textAlign))}>
                    {node.content?.map((child, i) => renderNode(child, i)) ?? <br />}
                </p>
            );
        }

        case "heading": {
            const level = (node.attrs?.level as number) ?? 2;
            const className = textAlignmentClass(node.attrs?.textAlign);
            const children = node.content?.map((child, i) => renderNode(child, i));
            if (level === 1) return <h1 key={index} className={className}>{children}</h1>;
            if (level === 3) return <h3 key={index} className={className}>{children}</h3>;
            return <h2 key={index} className={className}>{children}</h2>;
        }

        case "blockquote":
            return (
                <blockquote key={index}>
                    {node.content?.map((child, i) => renderNode(child, i))}
                </blockquote>
            );

        case "bulletList":
            return (
                <ul key={index}>
                    {node.content?.map((child, i) => renderNode(child, i))}
                </ul>
            );

        case "orderedList":
            return (
                <ol key={index}>
                    {node.content?.map((child, i) => renderNode(child, i))}
                </ol>
            );

        case "listItem":
            return (
                <li key={index}>
                    {node.content?.map((child, i) => renderNode(child, i))}
                </li>
            );

        case "figure": {
            const src = node.attrs?.src as string | undefined;
            if (!src) return null;
            // alt is null for imported images without alt text — keep the cast
            // honest so the `?? ""` actually does the nullish work.
            const alt = (node.attrs?.alt as string | null | undefined) ?? "";
            // Author-set width as a percentage of the column (null = full width).
            const width = node.attrs?.width as number | null | undefined;
            // Intrinsic dimensions (captured at upload) reserve layout space.
            const nw = node.attrs?.naturalWidth as number | null | undefined;
            const nh = node.attrs?.naturalHeight as number | null | undefined;
            const hasCaption = (node.content?.length ?? 0) > 0;
            const imgStyle: React.CSSProperties = {};
            if (width) imgStyle.width = `${width}%`;
            if (nw && nh) imgStyle.aspectRatio = `${nw} / ${nh}`;
            return (
                <figure key={index} className="my-8 text-center">
                    {/* Dimensions vary for author-uploaded images, so a plain
                        lazy <img> is used instead of next/image. referrerPolicy
                        avoids leaking the reader's URL to externally-hosted
                        (imported) images — privacy-first. */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        src={src}
                        alt={alt}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        style={imgStyle}
                        className="mx-auto h-auto max-w-full rounded-lg"
                    />
                    {hasCaption && (
                        <figcaption className="mt-2 text-sm italic text-muted-foreground">
                            {node.content?.map((child, i) => renderNode(child, i))}
                        </figcaption>
                    )}
                </figure>
            );
        }

        case "horizontalRule":
            return <hr key={index} />;

        case "hardBreak":
            return <br key={index} />;

        case "text":
            return (
                <span key={index}>{renderMarks(node.text ?? "", node.marks)}</span>
            );

        // Mentions render as plain text — just the name, no @ symbol
        case "mention":
            return (
                <span key={index}>
                    {(node.attrs?.label as string) ?? (node.attrs?.id as string) ?? ""}
                </span>
            );

        case "codeBlock":
            return (
                <pre key={index} className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 overflow-x-auto my-4 text-sm">
                    <code>
                        {node.content?.map((child, i) => renderNode(child, i))}
                    </code>
                </pre>
            );

        default:
            // Fallback: try to render children
            if (node.content) {
                return <div key={index}>{node.content.map((child, i) => renderNode(child, i))}</div>;
            }
            return null;
    }
}

/** Convenience entry point — render a full TipTap document to React nodes. */
export function renderTiptap(content: unknown): ReactNode {
    if (!content || typeof content !== "object") return null;
    return renderNode(content as TipTapNode, 0);
}
